import { token } from '@atlaskit/tokens';
import { collabStateManager } from '@databases/collab-state-manager';
import { getVersionId, useDatabaseContent } from '@databases/database-state';
import { isEmbed, isNativeEmbed } from '@databases/modes';
import shouldShowUpdatedSidePanel from '@databases/panel/shouldShowUpdatedSidePanel';
import usePanelState from '@databases/panel/usePanelState';
import { type DefaultRootState, defaultStoreUtils } from '@databases/redux';
import { deselectAll } from '@databases/redux/cellSelections/actions';
import { selectIsSoftSelectEnabled } from '@databases/redux/cellSelections/selectors';
import { type DatabaseLayoutType } from '@databases/types/database/view';
import { type DatabaseBridgeAPI, getBridgeAPI } from '@databases/utils/database/bridge';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { getExperimentValue, isFeatureFlagEnabled } from '@canvas/client-feature-flags';

import MainPanel, { ToolbarRenderer } from './MainPanel';
import PersistedPanelGroup from './PersistedPanelGroup';
import SidePanel from './SidePanel';
import { createPanelStorageKey } from './panel/utils';

type LayoutProps = {
  banners?: React.ReactNode;
  options?: {
    toolbarLocation: 'top' | 'views';
  };
  view: React.ReactNode;
  sidebar?: React.ReactNode | null;
  toolbar: React.ReactNode;
  outerContainerRef?: HTMLElement | null;
  isReadOnly?: boolean;
  layoutType?: DatabaseLayoutType;
};

const LayoutContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  height: 100%;
  position: absolute;
  width: 100%;
  overflow: hidden;
  left: 0;
  top: 0;
  margin-right: ${token('space.100')};

  #app-resize-handle {
    border-left-width: ${token('border.width.selected', '2px')};
    border-left-style: solid;
    border-left-color: ${token('color.border', '#091E4224')};
  }

  #app-resize-handle[data-resize-handle-state='hover'] {
    border-left-color: ${token('color.border.focused', '#388BFF')};
  }

  #app-resize-handle[data-resize-handle-state='drag'] {
    border-left-color: ${token('color.border.selected', '#0C66E4')};
  }

  #right-side-panel {
    height: 100%;
  }
`;

const ContentWrapper = styled.div`
  flex-grow: 1;
  min-height: 0;
  flex-shrink: 1;
  display: flex;
  flex-direction: row;
`;

const DatabaseLayout = ({
  banners,
  view,
  sidebar,
  toolbar,
  options = {
    toolbarLocation: 'top',
  },
  outerContainerRef,
  isReadOnly,
  layoutType,
}: LayoutProps) => {
  const isContentWrapperEnabled = isFeatureFlagEnabled(
    'databases-frontend-content-wrapper'
  );
  const [bridgeApi, setBridgeApi] = useState<DatabaseBridgeAPI | null>(null);
  const versionId = getVersionId();
  const [isRightGutterCollapsed, setIsRightGutterCollapsed] = useState<boolean>(false);
  const isEmbedView = isEmbed();
  const { getPanelKey, isPanelVisible } = usePanelState();
  const isNativeEmbedView = isNativeEmbed();
  const showUpdatedSidePanel = shouldShowUpdatedSidePanel();
  const { contentId } = useDatabaseContent();

  useEffect(() => {
    if (isContentWrapperEnabled) {
      const loadBridgeApi = async () => {
        await getBridgeAPI().then(setBridgeApi);
      };
      loadBridgeApi();
    }
  }, [isContentWrapperEnabled]);

  useEffect(() => {
    if (isContentWrapperEnabled && bridgeApi) {
      bridgeApi.objectSidebarMethods?.registerChangeObjectSidebarShownHandler(
        (isShown?: boolean) => {
          setIsRightGutterCollapsed(!!isShown);
        }
      );
    }
  }, [bridgeApi, isContentWrapperEnabled]);

  useEffect(() => {
    if (isContentWrapperEnabled) {
      const isViewingVersion = !!versionId;
      // collapse gutter when viewing a version
      setIsRightGutterCollapsed(isViewingVersion);
    }
  }, [isContentWrapperEnabled, versionId]);

  const panelKey = getPanelKey();
  const storageKey = useMemo(() => createPanelStorageKey(panelKey), [panelKey]);

  const dispatch = defaultStoreUtils.useDispatch();

  const isSoftSelected = defaultStoreUtils.useEqualSelector((state: DefaultRootState) => {
    return selectIsSoftSelectEnabled(state);
  });

  const collabManager = collabStateManager.getCollabManager(contentId ?? '');

  const isDatabasePresenceEnabled = getExperimentValue('databasePresence', 'enabled', {
    fireExperimentExposure: false,
  });

  return (
    <LayoutContainer
      id="layout-container"
      data-testid="layout-container"
      onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        const target = e.target as HTMLElement;
        if (
          'ariaLabel' in target &&
          target.ariaLabel !== 'dragHandlerIcon' &&
          target.hasAttribute('data-testid') &&
          target.getAttribute('data-testid') !== 'database-value-view-container'
        ) {
          if (!isSoftSelected) {
            if (collabManager && isDatabasePresenceEnabled) {
              collabManager.updateTelepointer(undefined, undefined);
            }
            dispatch(deselectAll());
          }
        }
      }}
    >
      {banners && <div data-testid="layout-banner-wrapper">{banners}</div>}
      {!showUpdatedSidePanel && options.toolbarLocation === 'top' ? (
        <ToolbarRenderer
          isEmbedView={isEmbedView}
          isNativeEmbedView={isNativeEmbedView}
          isContentWrapperEnabled={isContentWrapperEnabled}
          isRightGutterCollapsed={isRightGutterCollapsed}
          toolbar={toolbar}
          isSidePanelVisible={isPanelVisible()}
        />
      ) : null}

      <ContentWrapper data-testid="layout-content-wrapper">
        <PersistedPanelGroup
          storageKey={storageKey}
          outerContainerRef={outerContainerRef}
        >
          <MainPanel
            isEmbedView={isEmbedView}
            isNativeEmbedView={isNativeEmbedView}
            isContentWrapperEnabled={isContentWrapperEnabled}
            isRightGutterCollapsed={isRightGutterCollapsed}
            isSidePanelVisible={isPanelVisible()}
            options={options}
            view={view}
            toolbar={toolbar}
            isReadOnly={isReadOnly}
            layoutType={layoutType}
          />

          <SidePanel
            sidebar={sidebar}
            options={options}
            showUpdatedSidePanel={showUpdatedSidePanel}
            resizeDisabled={isNativeEmbedView}
            contentId={contentId}
          />
        </PersistedPanelGroup>
      </ContentWrapper>
    </LayoutContainer>
  );
};

export default DatabaseLayout;
